import {
  Form,
  ValidationContainer,
  ValidationMessage,
} from "@tandemdiabetes/sugarmate-react-components"
import escapeStringRegexp from "escape-string-regexp"
import {
  useCallback,
  useRef,
} from "react"
import type { ChangeEventHandler } from "react"

import type { AccountFormProps } from "../AccountFormProps"
import styles from "../AccountForms.module.scss"

import {
  LargePasswordInput,
  PasswordRequirements,
} from "src/components"
import { usePolyglot } from "src/contexts"
import { passwordPattern } from "src/utils"

const polyglotPrefix = "components.account_forms.password."
const passwordId = "password-id"
const passwordConfirmationId = "password-confirmation-id"

const passwordErrorId = "password-error-id"
const passwordConfirmationErrorId = "password-confirmation-error-id"

interface PasswordInfo {
  password: string;
  passwordConfirmation: string;
}

export function PasswordForm(props: AccountFormProps<PasswordInfo>) {
  const {
    children,
    onSubmit,
    form,
    updateForm,
  } = props

  const polyglot = usePolyglot()

  const hadPasswordOnFirstRender = useRef<boolean>(!!form.password)
  const hadPasswordConfirmationOnFirstRender = useRef<boolean>(!!form.passwordConfirmation)

  const onPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      updateForm({
        password: e.target.value,
      })
    },
    [updateForm],
  )

  const onPasswordConfirmationChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      updateForm({
        passwordConfirmation: e.target.value,
      })
    },
    [updateForm],
  )

  const passwordConfirmationPattern = `^(${escapeStringRegexp(form.password)})?$`

  return (
    <Form
      className={styles.form}
      onSubmit={onSubmit}
    >
      <div className={styles.heading}>
        {polyglot.t(`${polyglotPrefix}title`)}
      </div>
      <PasswordRequirements />
      <div className={styles.inputs}>
        <ValidationContainer
          validationMode={hadPasswordOnFirstRender.current ? "immediate" : "onFirstInteraction"}
        >
          <LargePasswordInput
            id={passwordId}
            placeholder={polyglot.t(`${polyglotPrefix}password.placeholder`)}
            className={styles.input}
            value={form.password}
            onChange={onPasswordChange}
            pattern={passwordPattern}
            autoComplete="new-password"
            minLength={8}
            maxLength={72}
            required
          />
          <ValidationMessage
            id={passwordErrorId}
            invalidMessage={{
              valueMissing: polyglot.t("validation_message.value_missing"),
              tooShort: polyglot.t("validation_message.password_too_short"),
              tooLong: polyglot.t("validation_message.password_too_long"),
              patternMismatch: polyglot.t("validation_message.password_invalid"),
            }}
          />
        </ValidationContainer>
        <ValidationContainer
          validationMode={hadPasswordConfirmationOnFirstRender.current ? "immediate" : "onFirstInteraction"}
        >
          <LargePasswordInput
            id={passwordConfirmationId}
            placeholder={polyglot.t(`${polyglotPrefix}password_confirmation.placeholder`)}
            className={styles.input}
            value={form.passwordConfirmation}
            onChange={onPasswordConfirmationChange}
            pattern={passwordConfirmationPattern}
            autoComplete="new-password"
            minLength={8}
            maxLength={72}
            required
          />
          <ValidationMessage
            id={passwordConfirmationErrorId}
            invalidMessage={{
              valueMissing: polyglot.t("validation_message.value_missing"),
              patternMismatch: polyglot.t("validation_message.password_mismatch"),
              tooShort: polyglot.t("validation_message.password_too_short"),
              tooLong: polyglot.t("validation_message.password_too_long"),
            }}
          />
        </ValidationContainer>
      </div>
      {children}
    </Form>
  )
}
